import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronUp } from "lucide-react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Navbar } from "@/components/landing/Navbar";
import { HeroSection } from "@/components/landing/HeroSection";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { BenchmarkingSystem } from "@/components/landing/BenchmarkingSystem";
import { LeaderboardPreview } from "@/components/landing/LeaderboardPreview";
import { QATools } from "@/components/landing/QATools";
import { DemoPlayground } from "@/components/landing/DemoPlayground";
import { ForCompanies } from "@/components/landing/ForCompanies";
import { Community } from "@/components/landing/Community";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { Footer } from "@/components/landing/Footer";

import FeaturesPage from "@/pages/FeaturesPage";
import PricingPage from "@/pages/PricingPage";
import DocumentationPage from "@/pages/DocumentationPage";
import ApiReferencePage from "@/pages/ApiReferencePage";
import SdkPage from "@/pages/SdkPage";
import ChangelogPage from "@/pages/ChangelogPage";
import AboutPage from "@/pages/AboutPage";
import CareersPage from "@/pages/CareersPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import CookiesPage from "@/pages/CookiesPage";
import BlogPage from "@/pages/BlogPage";
import LoginPage from "@/pages/LoginPage";
import HomePage from "@/pages/HomePage";
import AuthCallback from "@/pages/AuthCallback";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 600);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-arena-base noise-overlay">
      <Navbar />
      <main>
        <HeroSection />
        <HowItWorks />
        <BenchmarkingSystem apiUrl={API} />
        <LeaderboardPreview apiUrl={API} />
        <QATools apiUrl={API} />
        <DemoPlayground apiUrl={API} />
        <ForCompanies />
        <Community />
        <FinalCTA />
      </main>
      <Footer />

      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            data-testid="scroll-to-top-btn"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="fixed bottom-8 right-20 z-[999] p-3 bg-arena-elevated border border-white/10 rounded-md hover:border-arena-blue/50 transition-colors"
          >
            <ChevronUp className="w-5 h-5 text-white" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/home" element={<HomePage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/docs" element={<DocumentationPage />} />
      <Route path="/api-reference" element={<ApiReferencePage />} />
      <Route path="/sdk" element={<SdkPage />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/blog" element={<BlogPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
